import React from 'react';
import { Calendar, X } from 'lucide-react';
import { GenericColumn } from '../utils/tableColumns';
import { DateRangeFilter, DATE_PRESETS, getPresetLabel } from '../utils/datePresets';
import { formatShortThaiDateFromString } from '../../../utils/dateUtils';

// Helper function to format advanced filter display
const getAdvancedFilterDisplay = (filter: string): { isAdvanced: boolean; displayText: string } => {
    const advancedPatterns = [
        { prefix: 'contains:', label: 'มี' },
        { prefix: 'equals:', label: 'เท่ากับ' },
        { prefix: 'startsWith:', label: 'เริ่มด้วย' },
        { prefix: 'endsWith:', label: 'ลงท้ายด้วย' }
    ];

    for (const pattern of advancedPatterns) {
        if (filter.startsWith(pattern.prefix)) {
            const value = filter.substring(pattern.prefix.length);
            return {
                isAdvanced: true,
                displayText: `${pattern.label} "${value}"`
            };
        }
    }

    return {
        isAdvanced: false,
        displayText: filter
    };
};

interface FilterTagsProps<T> {
    filterState: { [key: string]: string[] };
    dateRangeFilters: { [key: string]: DateRangeFilter };
    columns: GenericColumn<T>[];
    handleFilterChange: (columnKey: string, value: string) => void;
    clearDateRangeFilter: (columnKey: string) => void;
    handleClearAllFilters: () => void;
    displayedItemsCount?: number; // New prop to show displayed items count
    totalItemsCount?: number;     // New prop to show total items count
}

const FilterTags = <T,>({
    filterState,
    dateRangeFilters,
    columns,
    handleFilterChange,
    clearDateRangeFilter,
    handleClearAllFilters,
    displayedItemsCount,
    totalItemsCount
}: FilterTagsProps<T>) => {
    // If there are no filters, don't render anything
    const hasFilters = Object.keys(filterState).length > 0 || Object.keys(dateRangeFilters).length > 0;

    if (!hasFilters) {
        return <></>;
    }

    return (
        <div className="flex flex-row justify-between items-center bg-gray-50 px-2">
            <div className="p-2 flex items-center">
                <span className="text-sm mr-2 text-gray-600">ตัวกรองที่ใช้งานอยู่:</span>
                <div className="flex flex-wrap gap-2">
                    {/* Regular and advanced filters */}
                    {Object.entries(filterState).map(([key, values]) => {
                        const column = columns.find(c => c.key === key);
                        return values.map(value => {
                            const { isAdvanced, displayText } = getAdvancedFilterDisplay(value);

                            return (
                                <span
                                    key={`${key}-${value}`}
                                    className={`${isAdvanced ? 'bg-indigo-100 text-indigo-800' : 'bg-blue-100 text-blue-800'} text-xs px-2 py-1 rounded-full flex items-center`}
                                >
                                    <span className="font-medium mr-1">{column?.label}:</span> {displayText}
                                    <button
                                        className="ml-1"
                                        onClick={() => handleFilterChange(key, value)}
                                    >
                                        <X size={14} />
                                    </button>
                                </span>
                            );
                        });
                    })}

                    {/* Date range filters */}
                    {Object.entries(dateRangeFilters).map(([key, { startDate, endDate }]) => {
                        const column = columns.find(c => c.key === key);
                        if (!startDate && !endDate) return null;

                        // Find if this matches any preset
                        const matchingPreset = DATE_PRESETS.find(preset => {
                            const presetRange = preset.getDateRange();
                            return presetRange.startDate === startDate && presetRange.endDate === endDate;
                        });

                        // Display friendly names for presets
                        let displayText = startDate && endDate ?
                            `${formatShortThaiDateFromString(startDate)} - ${formatShortThaiDateFromString(endDate)}` :
                            startDate ? `ตั้งแต่ ${formatShortThaiDateFromString(startDate)}` : `ถึง ${formatShortThaiDateFromString(endDate!)}`;

                        if (matchingPreset) {
                            displayText = getPresetLabel(matchingPreset);
                        }

                        return (
                            <span
                                key={`date-range-${key}`}
                                className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full flex items-center"
                            >
                                <Calendar size={12} className="mr-1" />
                                <span className="font-medium mr-1">{column?.label}:</span>
                                {displayText}
                                <button
                                    className="ml-1"
                                    onClick={() => clearDateRangeFilter(key)}
                                >
                                    <X size={14} />
                                </button>
                            </span>
                        );
                    })}

                    {/* Clear all filters button */}
                    <button
                        onClick={handleClearAllFilters}
                        className="text-xs text-gray-600 hover:text-gray-800 underline"
                    >
                        ล้างตัวกรองทั้งหมด
                    </button>
                </div>
            </div>
            <div className="p-2">
                {displayedItemsCount !== undefined && (
                    <span className="text-sm text-gray-600">
                        จำนวนรายการที่แสดง: {displayedItemsCount}
                        {totalItemsCount !== undefined && displayedItemsCount !== totalItemsCount &&
                            ` จาก ${totalItemsCount}`}
                    </span>
                )}
            </div>
        </div>
    );
};

export default FilterTags;
